import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button, Card, DatePicker, Empty, InputNumber, Popconfirm, Segmented, Select, Space, Table, Tabs, Tag, message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../../shared/services/apiClient';
import { formatMoney, currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import PageHeader from '../../../shared/components/PageHeader';

// Admin: VPC peerings (force delete), cross-AZ/region price matrix, traffic & revenue report, commits.

const BASE = '/api/admin/evm';
const call = async (method, url, data) => {
    try {
        const res = method === 'get' ? await apiClient.get(BASE + url, { params: data }) : await apiClient.post(BASE + url, data);
        return res.data || {};
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};
const CLASS = { same_az: '同可用区', cross_az: '跨可用区', cross_region: '跨地域' };
const GB = 1024 ** 3;
const fmtGB = (b) => `${((Number(b) || 0) / GB).toFixed(2)} GB`;

const Peerings = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState();
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        const res = await call('get', '/vpc-peering', { page, page_size: 20, status });
        setRows(res.data?.list || []);
        setTotal(res.data?.total || 0);
        setLoading(false);
    }, [page, status]);
    useEffect(() => { load(); }, [load]);
    const del = async (id) => {
        const res = await call('post', `/vpc-peering/${id}/delete`);
        res.status === 'success' ? message.success('已删除') : message.error(res.msg || '失败');
        load();
    };
    const side = (s) => <Space size={4}><span>VPC {s.vpc_id}</span><Tag>{s.cidr}</Tag><span>UID {s.user_id}{s.account ? ` · ${s.account}` : ''}</span></Space>;
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Select allowClear placeholder="状态" style={{ width: 120 }} value={status} onChange={(v) => { setStatus(v); setPage(1); }}
                    options={['pending', 'active', 'rejected', 'expired', 'deleted'].map((v) => ({ value: v, label: v }))} />
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table size="small" rowKey="id" loading={loading} dataSource={rows} scroll={{ x: 'max-content' }}
                pagination={{ current: page, total, pageSize: 20, onChange: setPage }}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: '发起方', render: (_, r) => side(r.requester) },
                    { title: '接受方', render: (_, r) => side(r.accepter) },
                    { title: '类型', dataIndex: 'cross', render: (c) => CLASS[c] || '-' },
                    { title: '状态', dataIndex: 'status', render: (s) => <Tag color={{ active: 'green', pending: 'orange' }[s]}>{s}</Tag> },
                    { title: '创建', dataIndex: 'created_at' },
                    {
                        title: '', width: 50,
                        render: (_, r) => (['pending', 'active'].includes(r.status) ? (
                            <Popconfirm title="强制删除？两端立即断开" onConfirm={() => del(r.id)}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        ) : null),
                    },
                ]} />
        </>
    );
};

const Prices = () => {
    const [data, setData] = useState({ prices: [], areas: [], zones: [] });
    const [rows, setRows] = useState([]);
    const [deleted, setDeleted] = useState([]);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        const res = await call('get', '/vpc-traffic/prices');
        const d = res.data || {};
        setData({ prices: d.prices || [], areas: d.areas || [], zones: d.zones || [] });
        setRows((d.prices || []).map((p, i) => ({ ...p, key: p.id || `n${i}` })));
        setDeleted([]);
    }, []);
    useEffect(() => { load(); }, [load]);
    const name = useMemo(() => ({
        zone: Object.fromEntries(data.zones.map((z) => [z.id, z.name])),
        area: Object.fromEntries(data.areas.map((a) => [a.id, a.name])),
    }), [data]);
    const set = (key, k, v) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [k]: v } : r)));
    const add = (scope) => setRows((rs) => [...rs, {
        key: `n${Date.now()}`, scope, class: scope === 'zone' ? 'cross_az' : 'cross_region', a_id: undefined, b_id: undefined, price_per_gb: 0, price_per_mbps_month: 0,
    }]);
    const save = async () => {
        setSaving(true);
        const res = await call('post', '/vpc-traffic/prices', { rows, delete: deleted });
        setSaving(false);
        if (res.status === 'success') {
            message.success('已保存');
            load();
        } else {
            message.error(res.msg || '保存失败');
        }
    };
    const pick = (r, k) => {
        if (r.scope === 'class') return '全部';
        const opts = r.scope === 'zone'
            ? data.zones.map((z) => ({ value: z.id, label: `${name.area[z.area_id] || z.area_id} / ${z.name}` }))
            : data.areas.map((a) => ({ value: a.id, label: a.name }));
        return <Select size="small" style={{ width: 180 }} value={r[k]} options={opts} onChange={(v) => set(r.key, k, v)} />;
    };
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Button icon={<PlusOutlined />} onClick={() => add('zone')}>可用区对</Button>
                <Button icon={<PlusOutlined />} onClick={() => add('area')}>地域对</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>保存</Button>
            </Space>
            <Table size="small" rowKey="key" pagination={false} dataSource={rows}
                columns={[
                    { title: '类型', dataIndex: 'class', render: (c, r) => `${CLASS[c]}${r.scope === 'class' ? '（默认）' : ''}` },
                    { title: 'A', render: (_, r) => pick(r, 'a_id') },
                    { title: 'B', render: (_, r) => pick(r, 'b_id') },
                    { title: `${currencySymbol(PLATFORM_CURRENCY)} / GB`, render: (_, r) => <InputNumber size="small" min={0} step={0.01} value={Number(r.price_per_gb)} onChange={(v) => set(r.key, 'price_per_gb', v ?? 0)} /> },
                    { title: `${currencySymbol(PLATFORM_CURRENCY)} / Mbps / 月`, render: (_, r) => <InputNumber size="small" min={0} step={1} value={Number(r.price_per_mbps_month)} onChange={(v) => set(r.key, 'price_per_mbps_month', v ?? 0)} /> },
                    {
                        title: '', width: 50,
                        render: (_, r) => (r.scope !== 'class' ? (
                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => {
                                if (r.id) setDeleted((d) => [...d, r.id]);
                                setRows((rs) => rs.filter((x) => x.key !== r.key));
                            }} />
                        ) : null),
                    },
                ]} />
        </>
    );
};

const Report = () => {
    const [group, setGroup] = useState('day');
    const [range, setRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const params = { group, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
    const load = useCallback(async () => {
        setLoading(true);
        const res = await call('get', '/vpc-traffic/report', params);
        setRows(res.data?.rows || []);
        setLoading(false);
    }, [group, range]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);
    const exportCsv = async () => {
        try {
            const res = await apiClient.get(`${BASE}/vpc-traffic/export`, { params, responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vpc-traffic-${group}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error('导出失败');
        }
    };
    const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    return (
        <>
            <Space style={{ marginBottom: 8 }} wrap>
                <Segmented value={group} onChange={setGroup} options={[{ value: 'day', label: '按天' }, { value: 'month', label: '按月' }]} />
                <DatePicker.RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} />
                <Button icon={<DownloadOutlined />} onClick={exportCsv}>导出</Button>
            </Space>
            <Table size="small" rowKey={(r) => `${r.period}|${r.class}`} loading={loading} dataSource={rows} pagination={{ pageSize: 50 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                summary={() => rows.length > 0 && (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>合计</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{fmtGB(sum('bytes'))}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{fmtGB(sum('commit_bytes'))}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5}>{formatMoney(sum('per_gb_amount'), PLATFORM_CURRENCY)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>{formatMoney(sum('commit_amount'), PLATFORM_CURRENCY)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
                columns={[
                    { title: '时间', dataIndex: 'period' },
                    { title: '类型', dataIndex: 'class', render: (c) => CLASS[c] || c },
                    { title: '流量', dataIndex: 'bytes', render: fmtGB },
                    { title: '承诺带宽内', dataIndex: 'commit_bytes', render: fmtGB },
                    { title: '峰值', dataIndex: 'peak_bps', render: (v) => `${(v / 1e6).toFixed(1)} Mbps` },
                    { title: '按量收入', dataIndex: 'per_gb_amount', render: (v, r) => formatMoney(v, r.currency || PLATFORM_CURRENCY) },
                    { title: '带宽收入', dataIndex: 'commit_amount', render: (v, r) => formatMoney(v, r.currency || PLATFORM_CURRENCY) },
                ]} />
        </>
    );
};

const Commits = () => {
    const [rows, setRows] = useState([]);
    useEffect(() => { call('get', '/vpc-traffic/commits').then((r) => setRows(r.data?.list || [])); }, []);
    return (
        <Table size="small" rowKey="id" dataSource={rows} scroll={{ x: 'max-content' }}
            columns={[
                { title: 'ID', dataIndex: 'id' },
                { title: 'UID', dataIndex: 'user_id' },
                { title: 'VPC', dataIndex: 'vpc_id' },
                { title: '对等连接', dataIndex: 'peering_id', render: (v) => v || '-' },
                { title: '范围', render: (_, c) => `${c.scope === 'area' ? '地域' : '可用区'} ${c.a_id} ↔ ${c.b_id}` },
                { title: 'Mbps', dataIndex: 'mbps' },
                { title: `${currencySymbol(PLATFORM_CURRENCY)}/月`, dataIndex: 'price_month' },
                { title: '状态', dataIndex: 'status', render: (s) => <Tag color={{ active: 'green', pending: 'orange' }[s]}>{s}</Tag> },
                { title: '到期', dataIndex: 'expires_at' },
                { title: '自动续费', dataIndex: 'auto_renew', render: (v) => (Number(v) ? '开' : '关') },
            ]} />
    );
};

const VpcPeeringAdmin = () => (
    <div>
        <PageHeader title="VPC 对等连接与跨区计费" />
        <Card>
            <Tabs items={[
                { key: 'peering', label: '对等连接', children: <Peerings /> },
                { key: 'prices', label: '价格', children: <Prices /> },
                { key: 'report', label: '报表', children: <Report /> },
                { key: 'commits', label: '承诺带宽', children: <Commits /> },
            ]} />
        </Card>
    </div>
);

export default VpcPeeringAdmin;
